/**
 * Animation utilities for page builder components
 */

import { IAnimationConfig } from "../types/pageBuilder.types";

/**
 * Get CSS animation properties from animation config
 */
export const getAnimationStyles = (
  animation: IAnimationConfig | undefined
): React.CSSProperties => {
  if (!animation || animation.type === "none") {
    return {};
  }

  const duration = animation.duration || 500;
  const delay = animation.delay || 0;
  const easing = animation.easing || "ease-in-out";
  const repeat = animation.repeat || false;
  const animations: Record<string, string> = {
    fade: `fadeIn ${duration}ms ${easing} ${delay}ms`,
    slide: `slideIn${animation.direction ? capitalize(animation.direction) : "Up"} ${duration}ms ${easing} ${delay}ms`,
    zoom: `zoomIn ${duration}ms ${easing} ${delay}ms`,
    bounce: `bounceIn ${duration}ms ${easing} ${delay}ms`,
    moveUpDown: `moveUpDown ${duration}ms ${easing} ${delay}ms ${repeat ? "infinite" : "0"}`,
  };

  return {
    animation: animations[animation.type] || animations.fade,
    animationFillMode: "both",
  };
};

/**
 * Capitalize first letter
 */
const capitalize = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Get CSS keyframes for animations
 */
export const getAnimationKeyframes = (): string => {
  return `
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes slideInUp {
      from { transform: translateY(20px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }

    @keyframes slideInDown {
      from { transform: translateY(-20px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }

    @keyframes slideInLeft {
      from { transform: translateX(-20px); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }

    @keyframes slideInRight {
      from { transform: translateX(20px); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }

    @keyframes zoomIn {
      from { transform: scale(0.9); opacity: 0; }
      to { transform: scale(1); opacity: 1; }
    }

    @keyframes bounceIn {
      0% { transform: scale(0.3); opacity: 0; }
      50% { transform: scale(1.05); }
      70% { transform: scale(0.9); }
      100% { transform: scale(1); opacity: 1; }
    }
  `;
};

/**
 * Check if animation should trigger on scroll
 */
export const shouldTriggerOnScroll = (animation: IAnimationConfig | undefined): boolean => {
  return animation?.trigger === "onScroll";
};

/**
 * Check if animation should trigger on hover
 */
export const shouldTriggerOnHover = (animation: IAnimationConfig | undefined): boolean => {
  return animation?.trigger === "onHover";
};

